'use client';;!function(){try{var e="undefined"!=typeof window?window:"undefined"!=typeof global?global:"undefined"!=typeof globalThis?globalThis:"undefined"!=typeof self?self:{};e._sentryModuleMetadata=e._sentryModuleMetadata||{},e._sentryModuleMetadata[(new e.Error).stack]=Object.assign({},e._sentryModuleMetadata[(new e.Error).stack],{"_sentryBundlerPluginAppKey:native-teams-frontend":true});}catch(e){}}();

import { RootLoading } from '@workspace/ui';
import { identifyPostHogUser, resetPostHogUser } from '@workspace/analytics/posthog/client';
import { identifySentryUser, resetSentryUser } from '@workspace/analytics/sentry/user';
import {
  ACCESSIBLE_APPS,
  isGuestOnlyRoute,
  isPublicRoute,
  subscribeToAuthTokenRefresh,
  subscribeToCrossAppAuthRefresh,
} from '@workspace/ui/lib';
import { useSessionExtension } from '@workspace/ui/hooks';
import type { JwtPayload, JwtUser } from '@workspace/ui/types';
import { jwtDecode } from 'jwt-decode';
import { usePathname, useRouter } from 'next/navigation';
import { useEffect, useLayoutEffect } from 'react';
import {
  clearAuthCookies,
  logout,
  refreshToken,
  syncAuthSessionFromCookies,
  syncAuthStateFromAccessToken,
} from '@/actions/mixed/auth';
import { GUEST_ONLY_ROUTES, PUBLIC_ROUTES } from '@/config/routes';
import { createJwtUserFromPayload, getActiveOrganisationFromUser } from '@/lib/active-organisation';
import { createEcho, disconnectEcho } from '@/lib/echo';
import { subscribeToOrganisationSwitch } from '@/lib/organisation-switch-channel';
import { subscribeToCrossAppOrganisationSwitch } from '@/lib/organisation-switch-marker';
import { useAuthStore } from '@/stores/authStore';

const ONBOARDING_CHECK_KEY = 'onboarding_check_completed';

export default function AuthBootstrap({
  initialAccessToken,
  hasRefreshToken,
  children,
}: {
  initialAccessToken?: string;
  hasRefreshToken: boolean;
  children: React.ReactNode;
}) {
  const accessToken = useAuthStore((s) => s.accessToken);
  const setAccessToken = useAuthStore((s) => s.setAccessToken);
  const setIsAuthenticated = useAuthStore((s) => s.setAuthenticated);
  const setIsLoading = useAuthStore((s) => s.setIsLoading);
  const setUser = useAuthStore((s) => s.setUser);
  const user = useAuthStore((s) => s.user);
  const isLoading = useAuthStore((s) => s.isLoading);
  const isOrganisationSwitching = useAuthStore((s) => s.isOrganisationSwitching);
  const userProfile = user?.user;
  const userEmail = userProfile?.email;
  const userFirstName = userProfile?.first_name;
  const userId = userProfile?.id;
  const userLastName = userProfile?.last_name;
  const userRoles = user?.roles;
  const userTeams = user?.teams;
  const accessibleApp = user?.accessible_app;
  const organisationId = getActiveOrganisationFromUser(user)?.id;

  const _router = useRouter();
  const pathname = usePathname();
  useSessionExtension({
    isEnabled: hasRefreshToken && !isGuestOnlyRoute(GUEST_ONLY_ROUTES, pathname),
    sessionVersion: accessToken,
    refreshSession: refreshToken,
  });

  useEffect(() => {
    const handleOrganisationSwitch = () => {
      window.location.reload();
    };

    const unsubscribeSameApp = subscribeToOrganisationSwitch(handleOrganisationSwitch);
    const unsubscribeCrossApp = subscribeToCrossAppOrganisationSwitch(handleOrganisationSwitch);
    const unsubscribeCrossAppAuthRefresh = subscribeToCrossAppAuthRefresh(
      syncAuthSessionFromCookies
    );

    return () => {
      unsubscribeSameApp();
      unsubscribeCrossApp();
      unsubscribeCrossAppAuthRefresh();
    };
  }, []);

  useEffect(
    () =>
      subscribeToAuthTokenRefresh((nextAccessToken) => {
        try {
          syncAuthStateFromAccessToken(nextAccessToken);
        } catch (error) {
          console.error('Failed to synchronize refreshed access token:', error);
        }
      }),
    []
  );

  // Layout effect so super properties are registered before children's mount-time
  // tracking effects run in the same commit.
  useLayoutEffect(() => {
    if (!userId || !userEmail) {
      // Resetting while the session is still loading would wipe persisted super
      // properties and rotate the distinct id / session on every hard load.
      if (isLoading) return;
      resetPostHogUser();
      resetSentryUser();
      return;
    }

    void identifyPostHogUser({
      app: 'wallet-app',
      id: userId,
      email: userEmail,
      firstName: userFirstName,
      lastName: userLastName,
      roles: userRoles,
      teams: userTeams,
      organisationId,
      accessibleApp,
    });

    identifySentryUser({
      app: 'wallet-app',
      id: userId,
      email: userEmail,
      firstName: userFirstName,
      lastName: userLastName,
      organisationId,
    });
  }, [
    accessibleApp,
    isLoading,
    organisationId,
    userEmail,
    userFirstName,
    userId,
    userLastName,
    userRoles,
    userTeams,
  ]);

  useEffect(() => {
    if (isOrganisationSwitching) return;

    // Don't trigger refresh for guest-only routes or when we have an access token
    if (isGuestOnlyRoute(GUEST_ONLY_ROUTES, pathname) || accessToken) {
      setIsLoading(false);
      return;
    }

    if (isPublicRoute(PUBLIC_ROUTES, pathname) && !initialAccessToken && !hasRefreshToken) {
      setIsLoading(false);
      return;
    }

    const abortController = new AbortController();

    const now = Date.now();
    const expireSession = () => {
      if (abortController.signal.aborted) return;

      localStorage.removeItem(ONBOARDING_CHECK_KEY);
      setAccessToken(null);
      setIsAuthenticated(false);
      setUser(null);
      void clearAuthCookies()
        .catch(() => undefined)
        .finally(() => {
          window.location.href = process.env.NEXT_PUBLIC_FE_URL_AUTH!;
        });
    };
    const refreshSession = () => {
      void refreshToken()
        .then((result) => {
          if (abortController.signal.aborted) return;
          if (!result.success && !result.isProcessing) expireSession();
        })
        .catch((error) => {
          console.error('Failed to refresh token:', error);
          expireSession();
        })
        .finally(() => {
          if (!abortController.signal.aborted) setIsLoading(false);
        });
    };

    // If we have an initial token, decode it to check expiration
    if (initialAccessToken) {
      try {
        const decodedToken = jwtDecode<JwtPayload>(initialAccessToken);

        const expirationTime = decodedToken.exp * 1000; // Convert to milliseconds

        const user: JwtUser = createJwtUserFromPayload(decodedToken);

        if (
          user.accessible_app !== ACCESSIBLE_APPS.WALLET &&
          user.accessible_app !== ACCESSIBLE_APPS.EMPLOYER &&
          user.accessible_app !== ACCESSIBLE_APPS.EMPLOYEE &&
          user.accessible_app !== ACCESSIBLE_APPS.CONTRACTOR &&
          user.accessible_app !== ACCESSIBLE_APPS.GIG_WORKER &&
          user.accessible_app !== ACCESSIBLE_APPS.INDIVIDUAL_CRAFT
        ) {
          // Logout and redirect
          logout().finally(() => {
            localStorage.removeItem('onboarding_check_completed');
            window.location.href = process.env.NEXT_PUBLIC_FE_URL_AUTH!;
          });

          return;
        }

        // Only refresh if we're within 1 minute of expiration
        if (expirationTime - now > 60 * 1000) {
          if (abortController.signal.aborted) return;

          setAccessToken(initialAccessToken);

          setIsAuthenticated(true);

          setUser(user);
          setIsLoading(false);

          return;
        } else {
          refreshSession();
        }
      } catch (error) {
        console.error('Failed to decode token:', error);

        if (hasRefreshToken) refreshSession();
        else expireSession();
      }
    } else if (hasRefreshToken) {
      refreshSession();
    } else {
      setIsLoading(false);
    }

    return () => {
      abortController.abort();
    };
  }, [
    pathname,
    accessToken,
    hasRefreshToken,
    setAccessToken,
    setIsAuthenticated,
    setIsLoading,
    setUser,
    initialAccessToken,
    isOrganisationSwitching,
  ]);

  // Initialize/disconnect Echo (Reverb) WebSocket based on auth state
  useEffect(() => {
    if (accessToken) {
      createEcho(accessToken);
    } else {
      disconnectEcho();
    }
    return () => {
      disconnectEcho();
    };
  }, [accessToken]);

  if (isLoading || isOrganisationSwitching) {
    return <RootLoading />;
  }

  return children;
}
